import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreateListingInput } from "@avestate/shared";
import { useCreateListing } from "@/hooks/useListings.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";

export default function ListingNew(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateListing();
  const [form, setForm] = useState({
    slug: "",
    kind: "immobile" as "immobile" | "cantiere",
    city: "",
    zone: "",
    province: "",
  });
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const parsed = CreateListingInput.safeParse({
      slug: form.slug.trim(),
      kind: form.kind,
      city: form.city.trim() || undefined,
      zone: form.zone.trim() || undefined,
      province: form.province.trim().toUpperCase() || undefined,
      country: "IT",
    });
    if (!parsed.success) {
      setErr(parsed.error.issues[0]?.message ?? "Dati non validi");
      return;
    }
    try {
      const created = await create.mutateAsync(parsed.data);
      toast.success(`Bozza creata: ${created.slug}`);
      navigate(`/listings/${created.id}`);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-[640px] space-y-6">
      <header>
        <p className="font-mono text-meta uppercase tracking-wider text-ink-mute">passo 1 di 1 · basics</p>
        <h1 className="mt-1 text-h1 font-display text-ink">Nuovo immobile</h1>
        <p className="text-body text-ink-soft">
          Compila lo slug e i dati base. Foto e dettagli si aggiungono nell'editor; l'assistente IA genera la bozza partendo dalle tue foto e appunti.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-5 rounded border border-rule bg-surface p-6">
        <div className="space-y-1.5">
          <Label htmlFor="slug">Slug (URL pubblico)</Label>
          <Input
            id="slug"
            placeholder="appartamento-vista-lago-peschiera"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            pattern="[a-z0-9-]+"
            required
          />
          <p className="text-meta text-ink-mute">Solo minuscole, numeri e trattini. Esempio: `villa-bardolino-3-camere`.</p>
        </div>

        <div className="space-y-1.5">
          <Label>Tipo</Label>
          <div className="flex gap-2">
            {(["immobile", "cantiere"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setForm({ ...form, kind: k })}
                className={`h-9 rounded-pill border px-4 font-mono text-meta uppercase tracking-wider ${
                  form.kind === k ? "border-accent bg-accent text-accent-on" : "border-rule bg-surface-2 text-ink-soft"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="city">Città</Label>
            <Input id="city" placeholder="Peschiera del Garda" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="zone">Zona</Label>
            <Input id="zone" placeholder="Centro storico" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="province">Provincia (sigla)</Label>
          <Input
            id="province"
            placeholder="VR"
            maxLength={2}
            value={form.province}
            onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase() })}
            className="w-[100px]"
          />
        </div>

        {err ? (
          <p className="rounded border border-risk/30 bg-risk/10 px-3 py-2 text-body text-risk">{err}</p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => navigate("/listings")}>Annulla</Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creazione..." : "Crea bozza e apri editor"}
          </Button>
        </div>
      </form>
    </div>
  );
}
